import { QuoteStatus, RequestForQuoteStatus } from '~/graphql/graphql';
import { isClosedRequestForQuoteStatus } from '~/utils/requestForQuoteClosed';

const AWARDABLE_QUOTE_STATUSES: ReadonlySet<QuoteStatus> = new Set([
  QuoteStatus.Submitted,
  QuoteStatus.PartiallyAccepted,
]);

const AWARDED_QUOTE_STATUSES: ReadonlySet<QuoteStatus> = new Set([
  QuoteStatus.Accepted,
  QuoteStatus.PartiallyAccepted,
]);

export function isQuoteAwardable(status: QuoteStatus): boolean {
  return AWARDABLE_QUOTE_STATUSES.has(status);
}

type FulfillableRequestForQuote = {
  status: RequestForQuoteStatus;
  items: ReadonlyArray<{ platformProductsLength: { id: string } }>;
};

export type FulfillableQuoteItem = {
  order?: { id: string } | null;
  platformProductsLength: { id: string };
};

type SelectingOrder<Item> = 'order' extends keyof Item ? Item : never;

export type FulfillableQuote<Item extends FulfillableQuoteItem> = {
  status: QuoteStatus;
  items: ReadonlyArray<SelectingOrder<Item>>;
};

function isEveryRequestedLineOrdered<Item extends FulfillableQuoteItem>(
  requestForQuote: FulfillableRequestForQuote,
  quotes: ReadonlyArray<FulfillableQuote<Item>>
): boolean {
  const orderedPlatformProductsLengthIds = new Set(
    quotes.flatMap((quote) =>
      quote.items
        .filter((item) => item.order != null)
        .map((item) => item.platformProductsLength.id)
    )
  );
  return (
    requestForQuote.items.length > 0 &&
    requestForQuote.items.every((item) =>
      orderedPlatformProductsLengthIds.has(item.platformProductsLength.id)
    )
  );
}

export function isRequestForQuoteFulfillable<Item extends FulfillableQuoteItem>(
  requestForQuote: FulfillableRequestForQuote,
  quotes: ReadonlyArray<FulfillableQuote<Item>>
): boolean {
  return (
    !isClosedRequestForQuoteStatus(requestForQuote.status) &&
    (quotes.some((quote) => quote.status === QuoteStatus.Accepted) ||
      isEveryRequestedLineOrdered(requestForQuote, quotes))
  );
}

export function isRequestForQuoteArchivable(
  status: RequestForQuoteStatus,
  quotes: ReadonlyArray<{ status: QuoteStatus }>
): boolean {
  return (
    status !== RequestForQuoteStatus.InProgress &&
    !isClosedRequestForQuoteStatus(status) &&
    !quotes.some((quote) => AWARDED_QUOTE_STATUSES.has(quote.status))
  );
}
